import {TemplateAssetType} from 'common/src/modules/dynamicParameters/dynamicParameters.enums';
import {MovieLayerType} from 'common/src/modules/movie/movie.enums';
import {
  MovieImageLayer,
  MovieShapeLayer,
  MovieTextLayer,
  MovieVideoLayer,
} from 'common/src/modules/movie/movieLayers.types';
import simpleMovieValue from 'common/src/modules/movie/utils/simpleMovieValue';
import {LayerWithWorkingProps} from 'common/src/modules/movieEditor/movieEditor.types';
import {z} from 'zod';
import getShapeContentFromPrompt from '../../utils/getShapeContentFromPrompt';
import {CLStore, store} from 'common/src/redux/store';
import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import getTextContentFromPrompt from '../../utils/getTextContentFromPrompt';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';
import updateLayer from 'common/src/modules/creativeTemplates/store/creativeTemplatePage/formatMovies/actions/updateLayer';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';

// Schema for the tool input
const inputSchema = z.object({
  layerId: z.string().describe('ID of the layer to edit'),
  contentChangeDescription: z
    .string()
    .describe('Description of the changes to make to the layer content'),
  assetId: z.string().nullable().optional().describe('for image/video content changes only'),
  width: z
    .number()
    .nullable()
    .optional()
    .describe('Layer width in pixels (required for image/video/text layers)'),
  height: z
    .number()
    .nullable()
    .optional()
    .describe('Layer height in pixels (required for image/video/text layers)'),
});

type EditLayerContentParams = z.infer<typeof inputSchema>;

function editLayerContentTool(store: CLStore) {
  // Validation function
  function validateInput(params: EditLayerContentParams): string | null {
    const {layerId, contentChangeDescription} = params;

    if (!layerId.trim()) {
      return 'Layer ID is required';
    }

    if (!contentChangeDescription.trim()) {
      return 'Content change description is required';
    }

    // Note: assetId, width, height validation will be done per layer type
    return null;
  }

  return tool({
    name: AdBuilderAgentToolName.EditLayerContent,
    displayName: 'Edit Layer Content',
    description: 'Edit the content of an existing layer (shape, image, video, text)',
    inputSchema,
    execute: async ({input: params, abortSignal}: ToolExecuteParams<typeof inputSchema>) => {
      try {
        const validationError = validateInput(params);

        if (validationError) {
          return {
            content: `Failed to edit layer content: ${validationError}`,
          };
        }

        const {contentChangeDescription, assetId, width, height} = params;

        const activeComp = getActiveComp(store);

        if (!activeComp) {
          return {
            content: 'ERROR: No active composition found',
          };
        }

        const layer = activeComp.l.find((l: LayerWithWorkingProps) => l.id === params.layerId);

        if (!layer) {
          return {
            content: `ERROR: Layer with ID "${params.layerId}" not found`,
          };
        }

        // Check if layer is locked (leveraging LayerWithWorkingProps)
        if (layer.isLocked) {
          return {
            content: `ERROR: Layer "${layer.name}" is locked and cannot be edited`,
          };
        }

        // Switch based on layer type
        switch (layer.ty) {
          case MovieLayerType.Shape:
            return await handleShapeLayerContentEdit({
              layer: layer as LayerWithWorkingProps<MovieShapeLayer>,
              contentChangeDescription,
              layerId: params.layerId,
              abortSignal,
            });

          case MovieLayerType.Image:
            return handleImageLayerContentEdit({
              layer: layer as LayerWithWorkingProps<MovieImageLayer>,
              contentChangeDescription,
              assetId,
              width,
              height,
              store,
            });

          case MovieLayerType.Video:
            return handleVideoLayerContentEdit({
              layer: layer as LayerWithWorkingProps<MovieVideoLayer>,
              contentChangeDescription,
              assetId,
              width,
              height,
              store,
            });

          case MovieLayerType.Text:
            return await handleTextLayerContentEdit({
              layer: layer as LayerWithWorkingProps,
              contentChangeDescription,
              abortSignal,
              store,
            });

          default:
            return {
              content: `Failed to edit layer content: Layer type ${layer.ty} is not supported for content editing`,
            };
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const trimmedError =
          errorMessage.length > 200 ? errorMessage.substring(0, 200) + '...' : errorMessage;

        return {
          content: `Failed to edit layer content: ${trimmedError}`,
        };
      }
    },
  });
}

// Handler for shape layer content editing
async function handleShapeLayerContentEdit({
  layer,
  contentChangeDescription,
  layerId,
  abortSignal,
}: {
  layer: LayerWithWorkingProps<MovieShapeLayer>;
  contentChangeDescription: string;
  layerId: string;
  abortSignal: AbortSignal;
}): Promise<{content: string}> {
  try {
    // Get the current shape (first shape in shapes array)
    const currentShape = layer.shape?.shapes?.[0];

    if (!currentShape) {
      return {
        content: 'Failed to edit shape content: No shape found in the shape layer',
      };
    }

    // Use getShapeContentFromPrompt to modify the shape
    const result = await getShapeContentFromPrompt({
      shapeDescription: contentChangeDescription,
      existingShape: currentShape,
      time: 0, // time - could be made configurable
      abortSignal,
    });

    // Update the layer using the context's updateLayer function

    store.dispatch(
      updateLayer({
        layerId,
        data: {
          shape: {
            ...layer.shape,
            shapes: [result.shape],
          },
        },
      }),
    );

    return {
      content: `Successfully updated shape layer id "${layer.id}"${result.unsupportedRequest ? `. Note: ${result.unsupportedRequest}` : ''}`,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const trimmedError =
      errorMessage.length > 200 ? errorMessage.substring(0, 200) + '...' : errorMessage;

    return {
      content: `Failed to edit shape content: ${trimmedError}`,
    };
  }
}

// Placeholder handlers for other layer types
function handleImageLayerContentEdit({
  layer,
  assetId,
  width,
  height,
  store,
}: {
  layer: LayerWithWorkingProps<MovieImageLayer>;
  contentChangeDescription: string;
  assetId?: string | null;
  width?: number | null;
  height?: number | null;
  store: CLStore;
}): {content: string} {
  const imageLayer = layer as MovieImageLayer;
  const changes: string[] = [];
  const layerUpdates: Partial<LayerWithWorkingProps<MovieImageLayer>> = {};
  const state = store.getState();

  // Update asset if provided
  if (assetId) {
    const templateAssets = state.creativeTemplatePage.movieAssets.templateAssets;

    const selectedImageAsset = templateAssets.find(
      (asset) => asset.id === assetId && asset.metadata.type === TemplateAssetType.Image,
    );

    if (!selectedImageAsset) {
      throw new Error(`Image asset with ID "${assetId}" not found`);
    }

    const imageUrl = selectedImageAsset.metadata.url;

    if (!imageUrl) {
      throw new Error(`Image asset "${assetId}" does not have a valid URL`);
    }

    // Prepare image updates
    layerUpdates.image = {
      ...imageLayer.image,
      url: imageUrl.replace(/^https?:/, ''),
    };
    changes.push(`asset updated`);
  }

  // Update dimensions if provided
  if (width !== undefined || height !== undefined) {
    const newWidth = width !== undefined ? (width ?? 0) : imageLayer.image.w;
    const newHeight = height !== undefined ? (height ?? 0) : imageLayer.image.h;

    layerUpdates.image = {
      ...(layerUpdates.image || imageLayer.image),
      w: newWidth,
      h: newHeight,
      naturalWidth: newWidth,
      naturalHeight: newHeight,
    };

    layerUpdates.w = newWidth;
    layerUpdates.h = newHeight;

    // Update anchor point to center
    layerUpdates.tr = {
      ...imageLayer.tr,
      an: simpleMovieValue([newWidth / 2, newHeight / 2, 0]),
    };

    if (width !== undefined) {
      changes.push(`width to ${width}px`);
    }

    if (height !== undefined) {
      changes.push(`height to ${height}px`);
    }
  }

  if (changes.length === 0) {
    return {
      content: `no changes made. Please specify assetId, width, or height to update.`,
    };
  }

  // Apply updates using context's updateLayer method
  store.dispatch(
    updateLayer({
      layerId: layer.id,
      data: layerUpdates,
    }),
  );

  return {
    content: `Updated image layer id "${layer.id}": ${changes.join(', ')}`,
  };
}

function handleVideoLayerContentEdit({
  layer,
  assetId,
  width,
  height,
  store,
}: {
  layer: LayerWithWorkingProps<MovieVideoLayer>;
  contentChangeDescription: string;
  assetId?: string | null;
  width?: number | null;
  height?: number | null;
  store: CLStore;
}): {content: string} {
  const videoLayer = layer as MovieVideoLayer;
  const changes: string[] = [];
  const layerUpdates: Partial<LayerWithWorkingProps<MovieVideoLayer>> = {};

  // Update asset if provided
  if (assetId) {
    const templateAssets = store.getState().creativeTemplatePage.movieAssets.templateAssets;

    const selectedVideoAsset = templateAssets.find(
      (asset) => asset.id === assetId && asset.metadata.type === TemplateAssetType.Video,
    );

    if (!selectedVideoAsset) {
      throw new Error(`Video asset with ID "${assetId}" not found`);
    }

    const videoUrl = selectedVideoAsset.metadata.url;

    if (!videoUrl) {
      throw new Error(`Video asset "${assetId}" does not have a valid URL`);
    }

    // Prepare video updates
    layerUpdates.video = {
      ...videoLayer.video,
      url: videoUrl.replace(/^https?:/, ''),
    };
    changes.push(`updated video to "${selectedVideoAsset.metadata.name}"`);
  }

  // Update dimensions if provided
  if (width !== undefined || height !== undefined) {
    const newWidth = width !== undefined ? (width ?? 0) : videoLayer.video.w;
    const newHeight = height !== undefined ? (height ?? 0) : videoLayer.video.h;

    layerUpdates.video = {
      ...(layerUpdates.video || videoLayer.video),
      w: newWidth,
      h: newHeight,
    };

    layerUpdates.w = newWidth;
    layerUpdates.h = newHeight;

    // Update anchor point to center
    layerUpdates.tr = {
      ...videoLayer.tr,
      an: simpleMovieValue([newWidth / 2, newHeight / 2, 0]),
    };

    if (width !== undefined) {
      changes.push(`width to ${width}px`);
    }

    if (height !== undefined) {
      changes.push(`height to ${height}px`);
    }
  }

  if (changes.length === 0) {
    return {
      content: `no changes made. Please specify assetId, width, or height to update.`,
    };
  }

  // Apply updates using context's updateLayer method
  store.dispatch(
    updateLayer({
      layerId: layer.id,
      data: layerUpdates,
    }),
  );

  return {
    content: `Updated video layer id "${layer.id}": ${changes.join(', ')}`,
  };
}

async function handleTextLayerContentEdit({
  layer,
  contentChangeDescription,
  abortSignal,
  store,
}: {
  layer: LayerWithWorkingProps;
  contentChangeDescription: string;
  abortSignal: AbortSignal;
  store: CLStore;
}): Promise<{content: string}> {
  try {
    const textLayer = layer as LayerWithWorkingProps;
    const templateAssets = store.getState().creativeTemplatePage.movieAssets.templateAssets;

    // Get the text layer's current aeSrcTxt
    const currentAeSrcTxt = (textLayer as LayerWithWorkingProps<MovieTextLayer>).text?.aeSrcTxt;

    // Find a font asset to use for fallback
    const fontAsset = templateAssets.find(
      (asset) => asset.metadata.type === TemplateAssetType.Font || asset.metadata.family,
    );

    if (!fontAsset) {
      throw new Error('No font asset found for text layer editing');
    }

    // Use getTextContentFromPrompt to modify the text content
    const result = await getTextContentFromPrompt({
      contentDescription: contentChangeDescription,
      width: null,
      height: null,
      fontMetadata: fontAsset.metadata,
      existingAeSrcTxt: currentAeSrcTxt,
      abortSignal,
    });

    // Update the layer using the context's updateLayer function
    store.dispatch(
      updateLayer({
        layerId: layer.id,
        data: {
          text: {
            ...(textLayer as LayerWithWorkingProps<MovieTextLayer>).text,
            aeSrcTxt: result.aeSrcTxt,
          },
        },
      }),
    );

    return {
      content: `Successfully updated text layer id "${layer.id}"${result.unsupportedRequest ? `. Note: ${result.unsupportedRequest}` : ''}`,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const trimmedError =
      errorMessage.length > 200 ? errorMessage.substring(0, 200) + '...' : errorMessage;

    return {
      content: `Failed to edit text content: ${trimmedError}`,
    };
  }
}

export default editLayerContentTool;
